import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Play, RotateCcw, Timer, Globe, Languages, Skull, ArrowRight, ShieldCheck } from "lucide-react";
import { mockDoctors } from "../data/mockData";
import { CASES, GAME_TYPES, SEALED_NO, fileNo, verdictLabel } from "../data/sporeGames";
import { getProgress, nextSealedId } from "../lib/progress";
import { HeaderActions, SporeHeader, SporePage, sporeBtn } from "./SporeShell";

const Label = ({ children }) => <p className="font-tac text-[11px] tracking-[0.25em] text-slate-500 uppercase">{children}</p>;

const Dossier = ({ doctor }) => (
  <aside className="lg:col-span-2 rounded-sm border border-slate-800 bg-[#121824] overflow-hidden spore-fade-up">
    <div className="relative aspect-[4/3]">
      <img src={doctor.image} alt={doctor.name} className="w-full h-full object-cover object-top grayscale contrast-125" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#121824] to-transparent" />
      <div className="absolute bottom-4 left-5 right-5">
        <h1 data-testid="doctor-name" className="font-display font-bold text-3xl leading-tight">{doctor.name}</h1>
        <p className="text-sm text-slate-300 mt-1">{doctor.currentRole}</p>
      </div>
    </div>
    <div className="p-5 space-y-5">
      <div className="flex flex-wrap gap-2 font-tac text-xs">
        <span className="border border-slate-700 px-2.5 py-1 flex items-center gap-1.5 text-slate-300"><Globe className="h-3.5 w-3.5 text-cyan-400" />{doctor.nationality}</span>
        {doctor.languages.map((l) => (
          <span key={l} className="border border-slate-800 px-2.5 py-1 flex items-center gap-1.5 text-slate-400"><Languages className="h-3.5 w-3.5" />{l}</span>
        ))}
      </div>
      <div>
        <Label>Specialisation</Label>
        <p className="text-slate-200 mt-1.5">{doctor.specialization}</p>
      </div>
      <div>
        <Label>Personality</Label>
        <ul className="mt-2 space-y-1.5">
          {doctor.personalityTraits.map((t) => (
            <li key={t} className="text-sm text-slate-400 flex gap-2"><span className="text-[#FF2E63]">—</span>{t}</li>
          ))}
        </ul>
      </div>
    </div>
  </aside>
);

const Mission = ({ doctor, entry, record, onStart, onNext }) => {
  const type = GAME_TYPES[entry.game];
  return (
    <section data-testid="mission-panel" className="lg:col-span-3 rounded-sm border border-slate-800 bg-[#0E131F] p-6 sm:p-8 space-y-7 spore-fade-up" style={{ animationDelay: "80ms" }}>
      <div>
        <Label>Case file {record ? fileNo(entry) : SEALED_NO} // {entry.category}</Label>
        {record ? (
          <h2 data-testid="mission-symptom" className="font-display uppercase font-bold text-4xl sm:text-5xl text-slate-50 mt-3 leading-tight">
            {entry.symptom} <span className="text-slate-500">=</span>{" "}
            <span data-testid="mission-verdict" data-verdict={entry.infected ? "infected" : "clear"} className={entry.infected ? "text-[#FF2E63] spore-glow-red" : "text-emerald-400"}>
              {verdictLabel(entry)}{entry.infected ? "!!" : ""}
            </span>
          </h2>
        ) : (
          <h2 data-testid="mission-sealed" className="font-display uppercase font-bold text-4xl sm:text-5xl text-slate-200 mt-3">
            Symptom <span className="bg-slate-200 text-transparent select-none px-2">SEALED</span>
          </h2>
        )}
        <p className="text-slate-400 mt-4 leading-relaxed">
          {doctor.name} is only here for <span className="text-cyan-300 font-tac">{type.time} seconds</span>. Beat their test to reveal the symptom before they move on, and find out whether it means the patient is infected.
        </p>
      </div>
      <div className="rounded-sm border border-slate-800 bg-[#121824] p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <span data-testid="mission-game-type" className={`font-tac text-xs tracking-widest px-2.5 py-1.5 border rounded-sm ${type.badge}`}>{type.name.toUpperCase()}</span>
          <span className="font-tac text-xs text-slate-400 flex items-center gap-1.5"><Timer className="h-3.5 w-3.5" /> {type.time}s TIME LIMIT</span>
        </div>
        <p className="text-sm text-slate-300 leading-relaxed">{type.howTo}</p>
      </div>
      {record ? (
        <div className="space-y-3">
          <p data-testid="mission-finding" className={`border-l-2 pl-4 text-slate-200 leading-relaxed ${entry.infected ? "border-[#FF2E63]" : "border-emerald-400"}`}>“{entry.finding}”</p>
          <p className="font-tac text-xs text-slate-500 flex items-center gap-2">
            {entry.infected ? <Skull className="h-3.5 w-3.5 text-red-400" /> : <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />}
            {entry.infected ? `EST. SURVIVAL ${entry.survival}` : "NO SPORE ACTIVITY"} · BEST TIME {record.seconds.toFixed(1)}s
          </p>
        </div>
      ) : (
        <p className="border-l-2 border-slate-700 pl-4 text-slate-400 italic">Field note: “{entry.clues[0]}”</p>
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        <button type="button" data-testid="start-game-btn" onClick={onStart} className={`${record ? sporeBtn.ghost : sporeBtn.primary} sm:flex-1 py-3.5`}>
          {record ? <RotateCcw className="h-4 w-4" /> : <Play className="h-4 w-4" />} {record ? "Replay test" : "Start test"}
        </button>
        {record && onNext && (
          <button type="button" data-testid="next-sealed-btn" onClick={onNext} className={`${sporeBtn.primary} sm:flex-1 py-3.5`}>
            Next sealed file <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </section>
  );
};

const Background = ({ doctor }) => (
  <section className="grid md:grid-cols-3 gap-6 mt-10">
    <div className="md:col-span-2 space-y-2">
      <Label>Background</Label>
      <p className="text-slate-300 leading-relaxed">{doctor.background}</p>
      <Label>Communication style</Label>
      <p className="text-slate-400 leading-relaxed">{doctor.communicationStyle}</p>
    </div>
    <div className="space-y-2">
      <Label>Areas of focus</Label>
      {doctor.focusAreas.map((a) => (
        <p key={a} className="text-sm text-slate-300 border border-slate-800 bg-[#121824] px-3 py-2">{a}</p>
      ))}
    </div>
  </section>
);

const DoctorProfile = ({ currentUser, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const doctor = mockDoctors.find((d) => d.id === id);
  if (!doctor || !CASES[id]) return <Navigate to="/" replace />;
  const progress = getProgress();
  const nextId = nextSealedId(id, progress);

  return (
    <SporePage>
      <SporeHeader>
        <HeaderActions currentUser={currentUser} onLogout={onLogout} />
      </SporeHeader>
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <button type="button" data-testid="back-to-roster-btn" onClick={() => navigate("/")} className={sporeBtn.ghost}>
          <ArrowLeft className="h-3.5 w-3.5" /> Roster
        </button>
        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 mt-6">
          <Dossier doctor={doctor} />
          <Mission
            doctor={doctor}
            entry={CASES[id]}
            record={progress[id]}
            onStart={() => navigate(`/doctor/${id}/play`)}
            onNext={nextId ? () => navigate(`/doctor/${nextId}`) : null}
          />
        </div>
        <Background doctor={doctor} />
      </main>
    </SporePage>
  );
};

export default DoctorProfile;
